import { Component, OnInit, input, computed, signal, inject, effect, ChangeDetectionStrategy } from '@angular/core'
import { Checkout } from 'depoto-core/src/entities'
import { SchemaUtil } from '../../utils'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { ChartsStatsCheckoutService } from '../../services/entity-services/chartsStatsCheckout.service'
import { CoreService } from '../../services'
import { StorageKeyService } from '../../services/storage-key.service'

type DashboardViewSettings = {
  selectedTimespan?: string
  selectedTimespanByCheckoutId?: Record<string, string>
  [key: string]: unknown
}

const DEFAULT_TIMESPAN = 'month'

@Component({
  selector: 'app-charts-stats-checkout',
  templateUrl: './charts-stats-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChartsStatsCheckoutComponent implements OnInit {
  chartsStatsCheckoutService = inject(ChartsStatsCheckoutService)
  storageKey = this.keyService.getStorageKeyForEntity('dashboard')
  checkout = input<Checkout>()
  dateFrom = signal<string>('')
  dateTo = signal<string>('')
  timespans: string[] = ['thisDay', 'week', 'thisMonth', 'month', '3months', 'thisYear', 'year']
  selectedTimespan = signal<string>('month')
  isNoDataForChart = computed(() => {
    return (
      this.queryBestsellersData.data()?.length === 0 &&
      this.queryCarrierUsageData.data()?.length === 0 &&
      this.queryPaymentSalesData.data()?.length === 0
    )
  })

  readonly queryBestsellersData = injectQuery(() => ({
    queryKey: ['bestsellersData', this.checkout().id, this.dateFrom(), this.dateTo()],
    queryFn: async () => {
      return await this.chartsStatsCheckoutService
        .getStatsBestsellers(this.dateFrom(), this.dateTo(), this.checkout().id, 50, SchemaUtil.bestseller)
        .then(result => result.items)
    },
  }))

  readonly queryCarrierUsageData = injectQuery(() => ({
    queryKey: ['carrierUsageData', this.checkout().id, this.dateFrom(), this.dateTo()],
    queryFn: async () => {
      return await this.chartsStatsCheckoutService
        .getStatsCarrierUsage(this.dateFrom(), this.dateTo(), this.checkout().id, SchemaUtil.carrierUsage)
        .then(result => result.items)
    },
  }))

  readonly queryPaymentSalesData = injectQuery(() => ({
    queryKey: ['paymentSalesData', this.checkout().id, this.dateFrom(), this.dateTo()],
    queryFn: async () => {
      return await this.chartsStatsCheckoutService
        .getStatsPaymentSale(this.dateFrom(), this.dateTo(), this.checkout().id, SchemaUtil.paymentSales)
        .then(result => result.items)
    },
  }))

  constructor(
    protected core: CoreService,
    protected keyService: StorageKeyService
  ) {
    effect(() => {
      this.queryBestsellersData.data()
      this.queryCarrierUsageData.data()
      this.queryPaymentSalesData.data()
    })
  }

  ngOnInit() {
    this.setTimespan(this.getStoredTimespan())
  }

  setTimespan(timespan: string) {
    const validTimespan = this.isValidTimespan(timespan) ? timespan : DEFAULT_TIMESPAN
    this.selectedTimespan.set(validTimespan)
    this.persistTimespan(validTimespan)
    const from = new Date()
    const to = new Date()
    switch (this.selectedTimespan()) {
      case 'thisDay':
        break
      case 'week':
        from.setDate(from.getDate() - 7)
        break
      case 'thisMonth':
        from.setDate(1)
        break
      case 'month':
        from.setMonth(from.getMonth() - 1)
        break
      case '3months':
        from.setMonth(from.getMonth() - 3)
        break
      case 'thisYear':
        from.setDate(1)
        from.setMonth(0)
        break
      case 'year':
        from.setFullYear(from.getFullYear() - 1)
        break
    }
    this.dateFrom.set(`${from.toISOString().substring(0, 10)} 00:00:00`)
    this.dateTo.set(`${to.toISOString().substring(0, 10)} 23:59:59`)
  }

  private getStoredTimespan(): string {
    const view = this.getDashboardView()
    const checkoutTimespan = this.getTimespansByCheckoutId(view)[String(this.checkout().id)]

    if (this.isValidTimespan(checkoutTimespan)) {
      return checkoutTimespan
    }

    return this.isValidTimespan(view.selectedTimespan) ? view.selectedTimespan : DEFAULT_TIMESPAN
  }

  private persistTimespan(timespan: string): void {
    const view = this.getDashboardView()

    this.core.services.storage.set(this.storageKey, {
      ...view,
      selectedTimespanByCheckoutId: {
        ...this.getTimespansByCheckoutId(view),
        [String(this.checkout().id)]: timespan,
      },
    })
  }

  private getDashboardView(): DashboardViewSettings {
    const view = this.core.services.storage.getSync(this.storageKey)
    return view && typeof view === 'object' && !Array.isArray(view) ? view : {}
  }

  private getTimespansByCheckoutId(view: DashboardViewSettings): Record<string, string> {
    const timespans = view.selectedTimespanByCheckoutId
    return timespans && typeof timespans === 'object' && !Array.isArray(timespans) ? timespans : {}
  }

  private isValidTimespan(timespan: unknown): timespan is string {
    return typeof timespan === 'string' && this.timespans.includes(timespan)
  }
}
